/**
 * AIConsentModal.tsx
 * GDPR-compliant AI Coach consent modal
 * Shows before first AI interaction, stores consent in Supabase
 *
 * Migration Week 3: Privacy Foundation
 */

import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Brain,
  Shield,
  Lock,
  Eye,
  Download,
  Trash2,
  CheckCircle,
  X,
  ExternalLink
} from 'lucide-react';
import { supabase } from '../../lib/supabase';

interface AIConsentModalProps {
  isOpen: boolean;
  onConsent: (consented: boolean) => void;
  onClose: () => void;
}

export const AIConsentModal: React.FC<AIConsentModalProps> = ({
  isOpen,
  onConsent,
  onClose
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [showDetails, setShowDetails] = useState(false);

  const handleConsent = async (consented: boolean) => {
    setIsLoading(true);

    try {
      // Get current user
      const { data: { user } } = await supabase.auth.getUser();

      if (user) {
        // Update user profile with consent
        const { error } = await supabase
          .from('profiles')
          .update({
            ai_coach_enabled: consented,
            ai_coach_consent_at: consented ? new Date().toISOString() : null,
            ai_coach_consent_version: 'v1.0'
          })
          .eq('id', user.id);

        if (error) {
          console.error('Error saving consent:', error);
        } else {
          onConsent(consented);
        }
      }
    } catch (error) {
      console.error('Error in consent flow:', error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
          onClick={(e) => {
            if (e.target === e.currentTarget) onClose();
          }}
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            className="relative w-full max-w-2xl bg-gradient-to-br from-purple-900/95 to-pink-900/95 backdrop-blur-xl rounded-2xl border border-white/20 p-4 sm:p-6 md:p-8 max-h-[90vh] overflow-y-auto shadow-2xl"
          >
            {/* Close button */}
            <button
              onClick={onClose}
              className="absolute top-4 right-4 p-2 bg-black/40 hover:bg-black/60 rounded-full text-white hover:text-white transition-all"
              aria-label="Close"
            >
              <X className="w-5 h-5" />
            </button>

            {/* Header */}
            <div className="text-center mb-6">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-purple-600 to-pink-600 rounded-full mb-4">
                <Brain className="w-8 h-8 text-white" />
              </div>
              <h2 className="text-3xl font-bold text-white mb-2">
                AI Coach Available
              </h2>
              <p className="text-lg text-white/80">
                Your personalized journey companion
              </p>
            </div>

            {/* Privacy-first messaging */}
            <div className="space-y-4 mb-8">
              <div className="bg-black/30 backdrop-blur rounded-xl p-4 border border-white/10">
                <h3 className="text-lg font-semibold text-white mb-3">
                  What is the AI Coach?
                </h3>
                <p className="text-white/80 text-sm leading-relaxed">
                  Our AI Coach uses Google Gemini to provide personalized guidance throughout your LIFE, MONEY, and JOY journey.
                  It learns from your responses to offer contextual insights and suggestions tailored to your unique path.
                </p>
              </div>

              {/* Privacy features */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="bg-black/20 backdrop-blur rounded-lg p-4 border border-green-500/30">
                  <div className="flex items-start gap-3">
                    <Shield className="w-5 h-5 text-green-400 mt-0.5 flex-shrink-0" />
                    <div>
                      <h4 className="font-semibold text-white text-sm mb-1">Your Control</h4>
                      <p className="text-white/70 text-xs">
                        Enable or disable AI Coach anytime in Settings
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-black/20 backdrop-blur rounded-lg p-4 border border-blue-500/30">
                  <div className="flex items-start gap-3">
                    <Lock className="w-5 h-5 text-blue-400 mt-0.5 flex-shrink-0" />
                    <div>
                      <h4 className="font-semibold text-white text-sm mb-1">Data Privacy</h4>
                      <p className="text-white/70 text-xs">
                        Only necessary context sent. No PII shared.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-black/20 backdrop-blur rounded-lg p-4 border border-purple-500/30">
                  <div className="flex items-start gap-3">
                    <Download className="w-5 h-5 text-purple-400 mt-0.5 flex-shrink-0" />
                    <div>
                      <h4 className="font-semibold text-white text-sm mb-1">Export Data</h4>
                      <p className="text-white/70 text-xs">
                        Download all your data anytime (GDPR compliant)
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-black/20 backdrop-blur rounded-lg p-4 border border-red-500/30">
                  <div className="flex items-start gap-3">
                    <Trash2 className="w-5 h-5 text-red-400 mt-0.5 flex-shrink-0" />
                    <div>
                      <h4 className="font-semibold text-white text-sm mb-1">Delete Data</h4>
                      <p className="text-white/70 text-xs">
                        Request deletion of all your AI data
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Transparency toggle */}
              <button
                onClick={() => setShowDetails(!showDetails)}
                className="w-full text-left bg-black/20 backdrop-blur rounded-lg p-4 border border-white/10 hover:border-white/20 transition-colors"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Eye className="w-5 h-5 text-white/60" />
                    <span className="text-white font-medium">
                      {showDetails ? 'Hide' : 'Show'} technical details
                    </span>
                  </div>
                  <motion.div
                    animate={{ rotate: showDetails ? 180 : 0 }}
                    transition={{ duration: 0.2 }}
                  >
                    <CheckCircle className="w-5 h-5 text-white/40" />
                  </motion.div>
                </div>
              </button>

              <AnimatePresence>
                {showDetails && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    className="bg-black/30 backdrop-blur rounded-lg p-4 border border-white/10 overflow-hidden"
                  >
                    <h4 className="font-semibold text-white mb-3 text-sm">
                      What data is sent to Google Gemini?
                    </h4>
                    <ul className="space-y-2 text-xs text-white/70">
                      <li className="flex items-start gap-2">
                        <span className="text-green-400 mt-0.5">✓</span>
                        <span>Your current journey step and module progress</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-green-400 mt-0.5">✓</span>
                        <span>Last 5 conversation messages for context</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-green-400 mt-0.5">✓</span>
                        <span>Summary of your recent activities (stories, decisions, connections)</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-red-400 mt-0.5">✗</span>
                        <span>Your name, email, or personally identifiable information</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-red-400 mt-0.5">✗</span>
                        <span>Full conversation history (only last 5 messages)</span>
                      </li>
                    </ul>
                    <div className="mt-4 pt-4 border-t border-white/10">
                      <a
                        href="https://ai.google.dev/gemini-api/terms"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 text-xs text-purple-400 hover:text-purple-300 transition-colors"
                      >
                        <ExternalLink className="w-3 h-3" />
                        Google Gemini API Terms & Privacy
                      </a>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Action buttons */}
            <div className="flex flex-col sm:flex-row gap-3">
              <button
                onClick={() => handleConsent(true)}
                disabled={isLoading}
                className="flex-1 px-6 py-4 min-h-[56px] bg-gradient-to-r from-purple-600 to-pink-600 text-white font-semibold rounded-xl hover:shadow-lg hover:shadow-purple-500/50 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed touch-manipulation"
              >
                {isLoading ? (
                  <span className="flex items-center justify-center gap-2">
                    <motion.div
                      animate={{ rotate: 360 }}
                      transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                      className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full"
                    />
                    Saving...
                  </span>
                ) : (
                  <span className="flex items-center justify-center gap-2">
                    <CheckCircle className="w-5 h-5" />
                    Enable AI Coach
                  </span>
                )}
              </button>

              <button
                onClick={() => handleConsent(false)}
                disabled={isLoading}
                className="flex-1 px-6 py-4 min-h-[56px] bg-white/10 backdrop-blur text-white font-semibold rounded-xl hover:bg-white/20 transition-all duration-300 border border-white/20 disabled:opacity-50 disabled:cursor-not-allowed touch-manipulation"
              >
                Continue without AI
              </button>
            </div>

            {/* Legal note */}
            <p className="text-center text-xs text-white/50 mt-4">
              You can change your AI Coach preferences anytime in Settings.
              By enabling, you agree to our use of Google Gemini API for personalized guidance.
            </p>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default AIConsentModal;
